// src/pages/Agenda.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  getFirestore, collection, addDoc, getDocs, query, orderBy, Timestamp,
  updateDoc, deleteDoc, doc
} from 'firebase/firestore';
import { useEmpresaId } from '../context/EmpresaContext';
import Swal from 'sweetalert2';

import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import ModalNovoAgendamento from '../components/ModalNovoAgendamento';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const db = getFirestore();

const CustomToolbar = ({ label, onNavigate, onView, view }) => (
  <div className="p-4 flex flex-col sm:flex-row items-center justify-between bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
    <div className="flex items-center gap-2 mb-4 sm:mb-0">
      <button
        onClick={() => onNavigate('TODAY')}
        className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        Hoje
      </button>
      <div className="flex items-center">
        <button onClick={() => onNavigate('PREV')} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" title="Anterior">
          <ChevronLeft size={20} />
        </button>
        <button onClick={() => onNavigate('NEXT')} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" title="Próximo">
          <ChevronRight size={20} />
        </button>
      </div>
    </div>

    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 capitalize">{label}</h2>

    <div className="flex items-center bg-gray-100 dark:bg-gray-700 p-1 rounded-lg mt-4 sm:mt-0">
      <button
        onClick={() => onView('month')}
        className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${view === 'month' ? 'bg-white dark:bg-gray-900 shadow text-blue-600' : 'text-gray-600 dark:text-gray-300'}`}
      >
        Mês
      </button>
      <button
        onClick={() => onView('week')}
        className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${view === 'week' ? 'bg-white dark:bg-gray-900 shadow text-blue-600' : 'text-gray-600 dark:text-gray-300'}`}
      >
        Semana
      </button>
      <button
        onClick={() => onView('day')}
        className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${view === 'day' ? 'bg-white dark:bg-gray-900 shadow text-blue-600' : 'text-gray-600 dark:text-gray-300'}`}
      >
        Dia
      </button>
    </div>
  </div>
);

export default function Agenda() {
  const empresaId = useEmpresaId();

  const [events, setEvents] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [agendamentoEdit, setAgendamentoEdit] = useState(null);

  const colAgendamentos = (id) => collection(db, 'empresas', id, 'agendamentos');
  const colClientes = (id) => collection(db, 'empresas', id, 'clientes');
  const refAgendamento = (id, agId) => doc(db, 'empresas', id, 'agendamentos', agId);

  const fetchAgendamentosEClientes = useCallback(async (id) => {
    setLoading(true);
    try {
      const qAg = query(colAgendamentos(id), orderBy('data', 'asc'));
      const agSnap = await getDocs(qAg);

      const calendarEvents = agSnap.docs.map(d => {
        const data = d.data();
        const start = data?.data?.toDate ? data.data.toDate() : new Date();
        const dur = Number(data?.duracaoMin || 60);
        const end = moment(start).add(dur, 'minutes').toDate();
        return {
          id: d.id,
          title: `${data?.clienteNome || 'Cliente'} - ${data?.motivo || ''}`.trim(),
          start, end,
          resource: { ...data, duracaoMin: dur }
        };
      });
      setEvents(calendarEvents);

      const qCli = query(colClientes(id), orderBy('nome'));
      const cliSnap = await getDocs(qCli);
      setClientes(cliSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Erro ao buscar dados da agenda:', e);
      Swal.fire('Erro', 'Não foi possível carregar os agendamentos.', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (empresaId) fetchAgendamentosEClientes(empresaId);
    else setLoading(false);
  }, [empresaId, fetchAgendamentosEClientes]);

  const handleSelectSlot = useCallback((slotInfo) => {
    setSelectedSlot(slotInfo);
    setAgendamentoEdit(null);
    setIsModalOpen(true);
  }, []);

  const handleSelectEvent = useCallback((event) => {
    const data = event.resource;
    Swal.fire({
      title: event.title,
      html: `
        <p><strong>Cliente:</strong> ${data?.clienteNome || ''}</p>
        <p><strong>Telefone:</strong> ${data?.clienteTelefone || 'Não informado'}</p>
        <p><strong>Horário:</strong> ${moment(event.start).format('DD/MM/YYYY HH:mm')}</p>
        <p><strong>Status:</strong> ${data?.status || 'Agendado'}</p>
      `,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Editar',
      denyButtonText: 'Excluir',
      cancelButtonText: 'Fechar',
      icon: 'info'
    }).then(async (res) => {
      if (res.isConfirmed) {
        setSelectedSlot({ start: event.start });
        setAgendamentoEdit({ id: event.id, ...data });
        setIsModalOpen(true);
      } else if (res.isDenied) {
        const ok = await Swal.fire({
          title: 'Excluir agendamento?',
          text: 'Esta ação não poderá ser desfeita.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, excluir',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#d33'
        });
        if (ok.isConfirmed && empresaId) {
          try {
            await deleteDoc(refAgendamento(empresaId, event.id));
            Swal.fire('Excluído!', 'Agendamento removido.', 'success');
            fetchAgendamentosEClientes(empresaId);
          } catch (e) {
            console.error(e);
            Swal.fire('Erro', 'Não foi possível excluir.', 'error');
          }
        }
      }
    });
  }, [empresaId, fetchAgendamentosEClientes]);

  // Salvar (criar/editar)
  const handleSalvarAgendamento = async (form) => {
    if (!empresaId) return;

    if (!form.motivo?.trim()) {
      Swal.fire('Atenção', 'O campo "Motivo da Visita" é obrigatório.', 'warning');
      return;
    }

    try {
      const dataHora = new Date(`${form.data}T${form.hora}`);
      const payload = {
        clienteId: form.clienteId || null,
        clienteNome: form.clienteNome,
        clienteTelefone: form.clienteTelefone || '',
        motivo: form.motivo.trim(),
        data: Timestamp.fromDate(dataHora),
        status: form.status || 'Agendado',
        duracaoMin: Number(form.duracaoMin || 60),
      };

      if (agendamentoEdit?.id) {
        await updateDoc(refAgendamento(empresaId, agendamentoEdit.id), payload);
        Swal.fire('Atualizado!', 'Agendamento atualizado com sucesso.', 'success');
      } else {
        await addDoc(colAgendamentos(empresaId), { ...payload, criadoEm: Timestamp.now() });
        Swal.fire('Sucesso!', 'Agendamento criado com sucesso.', 'success');
      }

      setIsModalOpen(false);
      setAgendamentoEdit(null);
      fetchAgendamentosEClientes(empresaId);
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      Swal.fire('Erro', 'Não foi possível salvar o agendamento.', 'error');
    }
  };

  // Drag & Drop / Resize
  const persistMoveOrResize = async ({ event, start, end }) => {
    if (!empresaId) return;
    try {
      const duracaoMin = Math.max(15, Math.round((end - start) / 60000)); // mínimo 15 min
      await updateDoc(refAgendamento(empresaId, event.id), {
        data: Timestamp.fromDate(start),
        duracaoMin
      });
      fetchAgendamentosEClientes(empresaId);
    } catch (e) {
      console.error('Erro ao mover/redimensionar:', e);
      Swal.fire('Erro', 'Não foi possível atualizar o horário.', 'error');
    }
  };

  const onEventDrop = (args) => persistMoveOrResize(args);
  const onEventResize = (args) => persistMoveOrResize(args);

  // cores por status
  const eventPropGetter = (event) => {
    const status = (event?.resource?.status || 'Agendado').toLowerCase();
    let bg = '#2563eb'; // azul
    if (status === 'confirmado') bg = '#16a34a'; // verde
    if (status === 'concluído' || status === 'concluido') bg = '#6b7280'; // cinza
    return {
      style: {
        backgroundColor: bg,
        border: 'none',
        color: 'white'
      }
    };
  };

  // limites de horário
  const minTime = new Date(); minTime.setHours(8, 0, 0, 0);
  const maxTime = new Date(); maxTime.setHours(18, 0, 0, 0);

  const messages = {
    allDay: 'Dia todo',
    previous: 'Anterior',
    next: 'Próximo',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'Não há eventos neste período.',
    showMore: (t) => `+ Ver mais (${t})`,
  };

  return (
    <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Agenda</h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg" style={{ height: '80vh' }}>
        {loading ? (
          <p className="text-center p-10">A carregar calendário...</p>
        ) : (
          <DndProvider backend={HTML5Backend}>
            <DnDCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable
              defaultView="week"
              components={{ toolbar: CustomToolbar }}
              min={minTime}
              max={maxTime}
              step={30}
              timeslots={2}
              resizable
              onEventDrop={onEventDrop}
              onEventResize={onEventResize}
              eventPropGetter={eventPropGetter}
              messages={messages}
            />
          </DndProvider>
        )}
      </div>

      <ModalNovoAgendamento
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setAgendamentoEdit(null); }}
        onSave={handleSalvarAgendamento}
        clientes={clientes}
        selectedSlot={selectedSlot}
        agendamentoEdit={agendamentoEdit}
      />
    </div>
  );
}
